import React, { useEffect, useState } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaPhone, FaLock, FaEye, FaEyeSlash, FaUserTag } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { login } from "../../utils/auth";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import { useBranding } from "../../contexts/BrandingContext";
import InstitutePicker from "./InstitutePicker";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import vcFavIcon from "../../Assets/Images/Logos/simple_logo.png";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/favicon.png";
import studentImg from "../../Assets/Images/login_bg.png";
import "./Login.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};
const logo = isLmsCampus() ? lmsLogo : vcLogo;
const favIcon = isLmsCampus() ? lmsFavIcon : vcFavIcon;

const ROLES = [
  { key: "student", label: "Student", icon: "🎓" },
  { key: "instructor", label: "Instructor", icon: "👨‍🏫" },
  { key: "admin", label: "Admin", icon: "⚙️" },
  { key: "superadmin", label: "Super Admin", icon: "🛡️" },
];

const Login = () => {
  useForceLightTheme();
  const branding = useBranding();
  const { refreshBranding } = branding;
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.faviconUrl || branding.logoUrl || favIcon;
  const brandName = branding.brandName;
  const supportEmail = branding.supportEmail;

  const [selectedRole, setSelectedRole] = useState(null);
  const [creds, setCreds] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [institutes, setInstitutes] = useState(null); // multi-institute picker
  const navigate = useNavigate();

  // Show message if user was kicked out by single-session enforcement
  useEffect(() => {
    const reason = sessionStorage.getItem('logoutReason');
    if (reason) {
      setError(reason);
      sessionStorage.removeItem('logoutReason');
    }
  }, []);

  useEffect(() => {
    if (selectedRole) localStorage.setItem("selectedRole", JSON.stringify(selectedRole));
  }, [selectedRole]);

  useEffect(() => {
    const storedRole = localStorage.getItem("selectedRole");
    if (storedRole) setSelectedRole(JSON.parse(storedRole));
  }, []);

  const handleChange = (e) => {
    setCreds((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  // Core login call — extracted so the institute picker can re-invoke it
  const doLogin = async (extraPayload = {}) => {
    setLoading(true);
    setError("");
    try {
      const usertype = selectedRole?.key || "student";
      const payload = { ...creds, usertype, ...extraPayload };
      const { data } = await apiClient.post("/auth/login", payload);

      // Normal login success
      if (data.token) {
        setInstitutes(null);
        login(data);
        // Refresh branding now that we have the user's instituteId
        if (refreshBranding) refreshBranding();
        if (data.user.usertype === "tenantadmin") navigate("/sa");
        else if (data.user.usertype === "student") navigate("/s/");
        else if (data.user.usertype === "instructor") navigate("/i/");
        else if (data.user.usertype === "admin" || data.user.usertype === "superadmin") navigate("/dashboard");
        else navigate("/");
      } else {
        setError("Invalid response from server.");
      }
    } catch (err) {
      const resp = err?.response;
      // HTTP 300 — multi-institute picker
      if (resp?.status === 300 && resp?.data?.multiInstitute) {
        setInstitutes(resp.data.institutes);
      } else {
        setError(resp?.data?.message || "Login failed!");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await doLogin();
  };

  // User picked an institute from the picker
  const handleInstitutePick = (inst) => {
    setInstitutes(null);
    doLogin({ instituteId: inst.instituteId });
  };

  return (
    <div className="auth-page">
      {/* Mobile top navbar */}
      <div className="auth-mobile-navbar">
        <Link to="/" className="auth-mobile-navbar-logo">
          <img src={brandFavIcon} alt="Logo" />
          <span>{brandName}</span>
        </Link>
        <Link to="/signup" className="auth-mobile-navbar-btn">
          Sign Up
        </Link>
      </div>

      {/* Mobile hero section — visible only on mobile */}
      <div className="auth-mobile-hero">
        <div className="auth-mobile-hero-bg" />
        <div className="auth-mobile-hero-content">
          <div className="auth-mobile-hero-badge">
            <img src={brandFavIcon} alt="Logo" />
          </div>
          <h1 className="auth-mobile-hero-title">Welcome back</h1>
          <p className="auth-mobile-hero-sub">Sign in to continue your learning journey</p>
        </div>
      </div>

      <div className="auth-container">
        {/* Left: Branding Panel (desktop/tablet only) */}
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-badge">
              <img src={brandFavIcon} alt="Logo" className="auth-brand-icon" />
            </div>
            <h1 className="auth-brand-title">{brandName}</h1>
            <p className="auth-brand-subtitle">Your virtual classroom for learning and teaching</p>
            <div className="auth-brand-illustration">
              <img src={studentImg} alt="Learning illustration" />
            </div>
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Live interactive classes</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>AI-powered learning</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Track progress & performance</span>
              </div>
            </div>
          </div>
          <div className="auth-brand-footer">
            <img src={brandLogo} alt={brandName} className="auth-brand-logo" />
          </div>
        </div>

        {/* Right: Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-form-wrapper">
            <div className="auth-form-header">
              <h2>Welcome back</h2>
              <p>Sign in to continue your learning journey</p>
            </div>

            {/* Role Selector — dropdown on mobile, chips on desktop */}
            <div className="auth-role-selector">
              <label className="auth-role-label">Select your role</label>

              {/* Mobile dropdown */}
              <div className="auth-role-dropdown">
                <div className="auth-input-wrapper">
                  <FaUserTag className="auth-input-icon" />
                  <select
                    value={selectedRole?.key || ""}
                    onChange={(e) => {
                      const role = ROLES.find((r) => r.key === e.target.value);
                      setSelectedRole(role || null);
                    }}
                  >
                    <option value="" disabled>Choose your role</option>
                    {ROLES.map((role) => (
                      <option key={role.key} value={role.key}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Desktop/tablet chip grid */}
              <div className="auth-role-grid">
                {ROLES.map((role) => (
                  <button
                    key={role.key}
                    type="button"
                    className={`auth-role-chip ${selectedRole?.key === role.key ? "active" : ""}`}
                    onClick={() => setSelectedRole(role)}
                  >
                    <span className="auth-role-icon">{role.icon}</span>
                    <span>{role.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label>Phone Number</label>
                <div className="auth-input-wrapper">
                  <FaPhone className="auth-input-icon" />
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    name="phone"
                    autoComplete="off"
                    value={creds.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <div className="auth-label-row">
                  <label>Password</label>
                  <Link to="/forgot-password" className="auth-forgot-link">
                    Forgot password?
                  </Link>
                </div>
                <div className="auth-input-wrapper">
                  <FaLock className="auth-input-icon" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    name="password"
                    autoComplete="off"
                    value={creds.password}
                    onChange={handleChange}
                    required
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="danger" className="auth-alert">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                className="auth-submit-btn"
                disabled={loading || !selectedRole}
              >
                {loading ? <Spinner size="sm" animation="border" /> : "Sign In"}
              </Button>
            </Form>

            <div className="auth-footer-text">
              Don't have an account?{" "}
              <Link to="/signup" className="auth-action-link">
                Create account
              </Link>
            </div>

            {supportEmail && (
              <div className="auth-footer-text" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
                Need help?{" "}
                <a href={`mailto:${supportEmail}`} className="auth-action-link">
                  {supportEmail}
                </a>
              </div>
            )}

            <div className="auth-footer-logo">
              <img src={brandLogo} alt={brandName} />
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Institute Picker */}
      {institutes && (
        <InstitutePicker
          institutes={institutes}
          onSelect={handleInstitutePick}
          onCancel={() => setInstitutes(null)}
          loading={loading}
          title="Choose Your Institute"
        />
      )}
    </div>
  );
};

export default Login;
